import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import { Logo } from '../../components';
import css from './CMSPage.module.css';
const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Page-specific styling hooks. Add a pageId here to give that single CMS page
// its own scoped CSS class (see CMSPage.module.css) without affecting any
// other page.
const PAGE_SPECIFIC_CLASSES = {
  'vakantiewoning-verhuren': css.verhurenPage,
};

/* =====================================================================
   Content adjustments for "vakantiewoning-verhuren", so this page reads
   like bookya-verhuren.nl - done here in code because Sharetribe Console's
   own editor can't produce this layout (icon-card grids, custom sections)
   even once the text itself is right in Console.

   Everything here transforms a COPY of the real page-asset data fetched
   from Console at render time - the actual content in Console is never
   touched. If you edit the wording of any of the affected fields in
   Console later, these rules are written to degrade gracefully (find by
   matching known text - if that text is no longer found, the section/
   block is just left as Console has it, nothing throws).
   ===================================================================== */

// Small, exact text fixes so wording matches bookya-verhuren.nl. Applied to
// every string in the page data, so it doesn't matter which section/block a
// given piece of text lives in - if the OLD text isn't found, nothing
// happens (safe no-op), so this can't break if Console content changes.
// Note: the pricing section's old heading typo fix ("verassingen" ->
// "verrassingen") and the "60.000 volgers" -> "70.000+ volgers" fix that
// used to live here are gone - both pieces of text are now fully replaced
// with bookya-verhuren.nl's exact wording further down (restructurePricingSection,
// buildWaaromBookyaCards), so patching the old Console wording first would
// just be immediately overwritten.
const TEXT_FIXES = [
  // Outdated follower counts -> the number bookya-verhuren.nl currently shows
  ['65.000+', '70.000+'],
  // Button wording, to match bookya-verhuren.nl's consistent phrasing
  ['Ik meld me aan', 'Vrijblijvend aanmelden'],
];

const applyTextFixes = value => {
  if (typeof value === 'string') {
    return TEXT_FIXES.reduce((acc, [from, to]) => acc.split(from).join(to), value);
  }
  if (Array.isArray(value)) {
    return value.map(applyTextFixes);
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach(key => {
      out[key] = applyTextFixes(value[key]);
    });
    return out;
  }
  return value;
};

// Simple, hand-drawn line icons (generic shapes - coins, a wave, sync
// arrows, a megaphone) standing in for bookya-verhuren.nl's icon set, which
// isn't available as an asset here. Real static SVG files under /public
// (see public/static/verhuren-icons/), referenced by URL - NOT inline
// "data:" URIs. This app's image-field sanitizer (src/util/sanitize.js)
// deliberately blocks "data:" URLs everywhere images are rendered, as an XSS
// precaution, so a data-URI image is silently swapped for a broken link.
const ICON_URL = {
  rendement: '/static/verhuren-icons/rendement.svg',
  regels: '/static/verhuren-icons/regels.svg',
  kalender: '/static/verhuren-icons/kalender.svg',
  marketing: '/static/verhuren-icons/marketing.svg',
};

// Note: every variant listed here ends up as one candidate in the <img> srcSet that
// ResponsiveImage builds (without a plain "src" fallback). A srcSet whose candidates share a
// width descriptor is a parse error, and the browser then discards the whole candidate list,
// leaving an <img> with no source at all. So the variants below must have DISTINCT widths:
// one 1x and one 2x, pointing at the same scalable SVG.
const makeIconImage = url => ({
  id: 'icon',
  type: 'imageAsset',
  attributes: {
    variants: {
      square1x: { url, width: 96, height: 96 },
      square2x: { url, width: 192, height: 192 },
    },
  },
});

const findSectionIndexByTitle = (sections, substring) =>
  sections.findIndex(
    s => typeof s?.title?.content === 'string' && s.title.content.includes(substring)
  );

// The card grid section itself has no matching title in Console - the text
// "Geen regels" etc. belongs to a BLOCK inside the section, not the section's
// own title. So to find the right section, look at each section's blocks.
const findSectionIndexByBlockTitle = (sections, substring) =>
  sections.findIndex(
    s =>
      Array.isArray(s?.blocks) &&
      s.blocks.some(
        b => typeof b?.title?.content === 'string' && b.title.content.includes(substring)
      )
  );

// Finds a section by ANY text anywhere in it (title, description, or any
// block's title/text) - used for sections identified by body copy rather
// than a heading (e.g. the pricing section, found by its checklist text).
const findSectionIndexByAnyText = (sections, substring) =>
  sections.findIndex(s => {
    const inOwnFields =
      s?.title?.content?.includes?.(substring) || s?.description?.content?.includes?.(substring);
    const inBlocks =
      Array.isArray(s?.blocks) &&
      s.blocks.some(
        b => b?.title?.content?.includes?.(substring) || b?.text?.content?.includes?.(substring)
      );
    return inOwnFields || inBlocks;
  });

// Replaces one block's markdown "text" field wholesale, found by a substring
// that's unique to its current content - used where the change is bigger
// than a find/replace (reordering, adding, or dropping checklist items), so
// the whole field is rewritten rather than patched piece by piece. Safe
// no-op if the anchor text isn't found (Console content changed more than
// expected), same as every other transform on this page.
const replaceBlockMarkdown = (sections, anchor, newMarkdown) => {
  let replaced = false;
  const next = sections.map(s => {
    if (!Array.isArray(s?.blocks)) return s;
    const blocks = s.blocks.map(b => {
      if (!replaced && typeof b?.text?.content === 'string' && b.text.content.includes(anchor)) {
        replaced = true;
        return { ...b, text: { ...b.text, content: newMarkdown } };
      }
      return b;
    });
    return { ...s, blocks };
  });
  return replaced ? next : sections;
};

// Same idea as replaceBlockMarkdown above, but for a block's plain-text
// "title" field (heading1/2/3) instead of its markdown "text" field.
const replaceBlockTitle = (sections, anchor, newTitle) => {
  let replaced = false;
  const next = sections.map(s => {
    if (!Array.isArray(s?.blocks)) return s;
    const blocks = s.blocks.map(b => {
      if (!replaced && typeof b?.title?.content === 'string' && b.title.content.includes(anchor)) {
        replaced = true;
        return { ...b, title: { ...b.title, content: newTitle } };
      }
      return b;
    });
    return { ...s, blocks };
  });
  return replaced ? next : sections;
};

// The hero's Console text is a longer 2-paragraph version (an extra sentence
// about direct contact/free choice/0% commissie, already covered by the
// heading right above it). bookya-verhuren.nl's actual hero copy is shorter -
// one paragraph, ending in "Lees snel verder" rather than "Lees verder".
// Found by a sentence unique to the old (longer) version.
// Note: kept short and deliberately stops before the raw markdown's own
// "**direct en persoonlijk contact**" bold markers - the anchor is matched
// against the RAW markdown source (asterisks and all), not the rendered
// text, so a longer anchor that spans across those asterisks would never
// match (confirmed by inspecting the live page's raw source data).
const HERO_PARAGRAPH_ANCHOR = 'Op ons platform heb je';
// "0% commissie." is prepended here (rather than left as the H1's own third
// line - see HERO_TITLE_NEW below) as its own leading paragraph, separated
// by a blank line so it renders as its own <p> (see the markdown-blank-line
// gotcha noted elsewhere in this file). CSS then styles just that first <p>
// to look like a continuation of the heading (same size/weight, lime color)
// - see CMSPage.module.css, "#section-1-block-1 p:first-of-type". This is
// the only way to give that one line its own color: Console's heading
// fields render as plain text (no markdown/HTML support - confirmed via
// Field.js's `exposeContentAsChildren` for heading1/2/3), so a real color
// change for part of a heading has to happen by moving that text into a
// markdown-capable field instead, not by styling a sub-span of the H1.
const HERO_PARAGRAPH_MARKDOWN =
  '0% commissie.\n\nMeld je aan bij Bookya en bereik tienduizenden potentiële gasten die specifiek op zoek zijn naar een bijzonder, duurzaam verblijf in Nederland en België. Lees snel verder of ontwerp direct vrijblijvend je advertentie!';

// Drops "0% commissie." from the H1 itself now that it's rendered as its own
// lime-colored paragraph instead (see HERO_PARAGRAPH_MARKDOWN above) -
// otherwise it would appear twice. Anchor is the start of Console's actual
// raw title content (confirmed live: "Jouw vakantiehuis.\nJouw regels.\n0%
// commissie.", real `\n` characters, not markdown or `<br>` tags).
const HERO_TITLE_ANCHOR = 'Jouw vakantiehuis';
const HERO_TITLE_NEW = 'Jouw vakantiehuis.\nJouw regels.';

// New intro block matching bookya-verhuren.nl's "WAAROM BOOKYA" eyebrow +
// heading + intro paragraph, ahead of the 4-card grid. The "WAAROM BOOKYA"
// label itself is added in CSS (a small design label, not flowing copy).
const WAAROM_BOOKYA_INTRO_SECTION = {
  sectionId: 'verhuren-waarom-bookya-intro',
  sectionType: 'features',
  title: {
    fieldType: 'heading2',
    content: 'Jouw vakantiehuis verhuren, zonder gedoe',
  },
  description: {
    fieldType: 'paragraph',
    content:
      'Bookya is geen bemiddelaar of traditioneel boekingsplatform. We maken zelfstandig verhuren makkelijker, zodat je minder afhankelijk bent van grote platforms en zelf de regie houdt over je verhuur.',
  },
  blocks: [],
};

const NEW_CARD_RENDEMENT = {
  blockType: 'defaultBlock',
  blockId: 'verhuren-card-rendement',
  media: { fieldType: 'image', alt: '', image: makeIconImage(ICON_URL.rendement) },
  title: { fieldType: 'heading2', content: 'Meer rendement uit je verblijf' },
  text: {
    fieldType: 'markdown',
    content:
      'Ontvang onbeperkt aanvragen, houd 100% van je inkomsten zelf en behoud de volledige regie over je gastenverblijf.',
  },
};

const NEW_CARD_KALENDER = {
  blockType: 'defaultBlock',
  blockId: 'verhuren-card-kalender',
  media: { fieldType: 'image', alt: '', image: makeIconImage(ICON_URL.kalender) },
  title: {
    fieldType: 'heading2',
    content: 'Synchroniseert met je eigen kalender(s). Zo heb je geen extra werk.',
  },
  text: {
    fieldType: 'markdown',
    content:
      'Bookya synchroniseert automatisch met platforms zoals Booking.com en Airbnb. Zo voorkom je dubbele boekingen en houd je moeiteloos het overzicht.',
  },
};

// Turns the existing 3-block "Geen regels / Marketing / 0% commissie"
// section into bookya-verhuren.nl's 4-card "WAAROM BOOKYA" grid:
//  - drops the block with no WordPress equivalent (0% commissie is already
//    covered by the hero and the pricing section further down)
//  - gives the two remaining blocks a matching line icon AND
//    bookya-verhuren.nl's own (shorter/reworded) card text - Console's
//    original text for these two is longer/differently worded, so the whole
//    text field is replaced, not just patched
//  - adds the two missing cards (rendement, kalender) and a new intro
//    section ahead of the grid
// Matches by the (already text-fixed) title text rather than position, so
// it keeps working if blocks get reordered in Console; if the expected
// blocks aren't found (content restructured more than that in Console),
// this safely leaves the section exactly as Console has it.
const buildWaaromBookyaCards = sections => {
  const cardsIndex = findSectionIndexByBlockTitle(sections, 'Geen regels');
  if (cardsIndex === -1) return sections;

  const original = Array.isArray(sections[cardsIndex].blocks) ? sections[cardsIndex].blocks : [];
  const regelsBlock = original.find(b => b?.title?.content?.includes('Geen regels'));
  const marketingBlock = original.find(b => b?.title?.content?.includes('schijnwerpers'));
  if (!regelsBlock || !marketingBlock) return sections;

  const finalBlocks = [
    NEW_CARD_RENDEMENT,
    {
      ...regelsBlock,
      media: { fieldType: 'image', alt: '', image: makeIconImage(ICON_URL.regels) },
      text: {
        fieldType: 'markdown',
        content:
          'Direct, persoonlijk contact met gasten, de vrijheid om door te verwijzen naar je eigen website en je bepaalt zelf hoe je betalingen afhandelt: rechtstreeks met de gast of via ons veilige betaalsysteem.',
      },
    },
    NEW_CARD_KALENDER,
    {
      ...marketingBlock,
      title: { fieldType: 'heading2', content: 'Online marketing & promotie' },
      media: { fieldType: 'image', alt: '', image: makeIconImage(ICON_URL.marketing) },
      text: {
        fieldType: 'markdown',
        content:
          'Ons team van (online) marketingspecialisten staat klaar om jouw vakantiewoning te promoten bij precies de juiste doelgroep. Via krachtige e-mailmarketing en onze social kanalen met meer dan 70.000 volgers, zorgen wij dat jouw accommodatie altijd in de schijnwerpers staat!',
      },
    },
  ];

  const next = [...sections];
  next[cardsIndex] = { ...next[cardsIndex], blocks: finalBlocks };
  next.splice(cardsIndex, 0, WAAROM_BOOKYA_INTRO_SECTION);
  return next;
};

// SectionBuilder gives a section a DOM id of `section-${index + 1}` whenever
// it has no explicit sectionId of its own - based on its position in the
// array at render time. Since buildWaaromBookyaCards inserts a brand new
// section, every original section after the insertion point would shift by
// one position and silently get a DIFFERENT id (e.g. the pricing section
// would flip from "section-3" to "section-4") - breaking every CSS rule and
// sub-nav anchor link in CMSPage.module.css that targets those ids. Pinning
// each original section's id to its ORIGINAL position, before any section is
// inserted, keeps every existing id stable no matter what gets added later.
const pinSectionIds = sections =>
  sections.map((s, i) => ({ ...s, sectionId: s.sectionId || `section-${i + 1}` }));

// bookya-verhuren.nl's pricing section is one thing, not two: a centered
// heading + intro line, then a single white card containing the price
// (€149/jaar), the 7-item checklist, and the "Vrijblijvend aanmelden"
// button, all together. Console's content for this section is structured
// completely differently - the checklist has 6 items in different wording,
// there's no intro line, and the price itself is a circular *photo* (the
// figures are baked into the image's own pixels, not real text - confirmed
// via the img's naturalWidth/height and alt text, "Abbonnementspijs"). So
// this section is rebuilt in full: the "Geen verrassingen..." text (found as
// a BLOCK title - this section has no section-level header of its own,
// confirmed via DOM inspection: its heading sits inside BlockDefault_text,
// not inside a <header>) is promoted to a real SECTION title+description
// (matching how it's laid out on bookya-verhuren.nl, centered above the
// card), and the block itself becomes just the card: no title of its own
// (it's now on the section), no media (the old photo), and a text field
// that opens with the real €149/jaar price line - not an image - followed
// by the full matching checklist.
// Console's own text for this block actually has a typo - "Geen
// verassingen..." (single r), confirmed by inspecting the live page's raw
// source data - not "verrassingen" (correct spelling, double r). Matching
// against the (mis-spelled) ORIGINAL text needs its own anchor, kept
// separate from PRICING_SECTION_TITLE_ANCHOR below (which is the NEW,
// correctly-spelled title this section gets promoted to - see
// restructurePricingSection - and is what reorderDarkBeforePricing looks
// for afterwards, once that promotion has already happened).
const PRICING_SECTION_ORIGINAL_ANCHOR = 'Geen verassingen';
const PRICING_SECTION_TITLE_ANCHOR = 'Geen verrassingen';

// Note: a single newline between lines does NOT create a visual break in
// this app's markdown renderer (it's treated as a soft break / just a
// space) - only a BLANK line starts a new paragraph. So the price, the
// "per maand" line and the fine print each need their own blank line to
// render as separate elements rather than running together. The price
// line itself uses a markdown heading ("##") rather than a plain line, so
// it renders as a real (larger, bolder) heading matching bookya-verhuren.nl,
// instead of same-sized text as the rest of the card. Only the "€149"
// part lives here - bookya-verhuren.nl's real price renders "/ jaar" in a
// much smaller, muted size right after it, which a single markdown heading
// can't do (no inline spans) - CMSPage.module.css generates "/ jaar" as
// its own small ::after instead, so this heading holds only the number
// that would ever actually change.
const PRICING_CARD_MARKDOWN = `## €149

Dat is €12,41 per maand

Prijs is excl. btw en betaling is op jaarbasis

---

Geen commissies — jij houdt de volledige opbrengst

Een professionele presentatie op Bookya.nl

Reserveringen via je eigen website. Of maak gebruik van ons boekingssysteem

Een boekingskalender die altijd up-to-date is

Zelf de volledige regie over je inkomsten

Vrijheid om je eigen prijzen en acties te bepalen

Zichtbaarheid bij 70.000+ volgers`;

// Finds the pricing section by its (still-original, not yet replaced) block
// title, then replaces the section's title/description/block-1 text/media
// all in one pass - unlike the old two-step version of this transform, there
// is no later step that needs to re-find this section by its old title, so
// there's no risk of a later lookup silently failing once the title moves.
const restructurePricingSection = sections => {
  const idx = findSectionIndexByBlockTitle(sections, PRICING_SECTION_ORIGINAL_ANCHOR);
  if (idx === -1) return sections;
  const blocks = Array.isArray(sections[idx].blocks) ? sections[idx].blocks : [];
  if (blocks.length === 0) return sections;
  const [firstBlock, ...restBlocks] = blocks;
  const updatedFirstBlock = {
    ...firstBlock,
    title: null,
    media: null,
    text: { fieldType: 'markdown', content: PRICING_CARD_MARKDOWN },
  };
  const next = [...sections];
  next[idx] = {
    ...next[idx],
    title: { fieldType: 'heading2', content: 'Geen verrassingen.' },
    description: {
      fieldType: 'paragraph',
      content:
        'Promotie doen we op basis van een vast (jaarlijks) tarief. Zo weet je precies waar je aan toe bent.',
    },
    blocks: [updatedFirstBlock, ...restBlocks],
  };
  return next;
};

// bookya-verhuren.nl shows 3 stats side by side under the "content shoot"
// section's copy (70.000+ Instagram volgers / 2M+ weergaven per maand /
// 100K+ views per reel) - real numbers already public on bookya-verhuren.nl,
// not invented. This page's Console content only has the volgers count,
// inline in a paragraph - added here as 3 short blocks the CSS lays out in
// a row.
const STATS_BLOCKS = [
  {
    blockType: 'defaultBlock',
    blockId: 'verhuren-stat-volgers',
    title: { fieldType: 'heading2', content: '70.000+' },
    text: { fieldType: 'markdown', content: 'Instagram volgers' },
  },
  {
    blockType: 'defaultBlock',
    blockId: 'verhuren-stat-weergaven',
    title: { fieldType: 'heading2', content: '2M+' },
    text: { fieldType: 'markdown', content: 'Weergaven per maand, gemiddeld' },
  },
  {
    blockType: 'defaultBlock',
    blockId: 'verhuren-stat-views',
    title: { fieldType: 'heading2', content: '100K+' },
    text: { fieldType: 'markdown', content: 'Views per reel, vaak meer' },
  },
];

// The dark "content shoot" section's Console text runs as one continuous
// paragraph; bookya-verhuren.nl breaks it into two paragraphs, a real "##"
// H2 subheading ("Content shoot op locatie", in the middle of the flow -
// this section has no separate heading field of its own to put it in, so
// it's written inline as markdown, same technique used elsewhere on this
// page) and a real markdown link on its first "@SietsOpStap" (with the @)
// mention, pointing at the real Instagram profile, matching the live site.
// Found by the same 'SietsOpStap' anchor addStatsRow and
// reorderDarkBeforePricing use below - it still appears in this new text,
// so those keep working after this replacement runs.
const DARK_SECTION_MARKDOWN = `Steeds meer reizigers oriënteren zich via social media op hun volgende vakantie. Daarom zetten wij hier volop op in. Met inmiddels 70.000+ volgers op Instagram brengen wij jouw verblijf direct onder de aandacht bij een groot publiek dat actief op zoek is naar een bijzondere accommodatie.

Hiervoor kunnen wij bestaande content gebruiken. Maar wij begrijpen ook dat niet iedere verhuurder de tijd, middelen of ervaring heeft om zijn eigen content te maken.

## Content shoot op locatie

Ook hier kunnen wij helpen via een content-shoot op locatie. Hiervoor werken wij samen met [@SietsOpStap](https://www.instagram.com/sietsopstap/). Een ervaren content creator die de sfeer en beleving van jouw verblijf op een hoogwaardige manier kan vastleggen.

Het video materiaal kan vervolgens worden ingezet op ons eigen kanaal én via het account van SietsOpStap, waardoor het bereik verder wordt vergroot. Ook kunnen we hiermee creatieve campagnes opzetten, zoals winacties of andere promoties.`;

// bookya-verhuren.nl's real "We helpen je graag!" intro text breaks cleanly
// after the first sentence ("...bij Bookya.nl?") onto its own line, rather
// than wrapping wherever the column happens to be narrow - Ron pointed out
// this page's version was instead just wrapping naturally mid-sentence.
// Console's own text for this block is one plain sentence-pair with no line
// break in it at all (confirmed live via the raw pageAssetsData, not just
// the rendered text - no bold/formatting marks either, so a plain anchor
// match is safe here).
// A markdown *hard line break* (a backslash immediately before the newline,
// standard CommonMark syntax) renders as a real <br> within the SAME <p> -
// unlike a blank line (which starts a whole new <p>, with this app's own
// spacing rule adding 24px of margin between paragraphs - too much gap for
// what should read as one continuous line-height break, not a new
// paragraph). rehype-sanitize's default schema allows <br>, so this doesn't
// need any extra CSS to look right - it's styled exactly like the rest of
// the paragraph's own wrapped lines already are.
const SUPPORT_PARAGRAPH_ANCHOR = 'Heb je nog vragen rondom het verhuren bij Bookya.nl?';
const SUPPORT_PARAGRAPH_MARKDOWN =
  'Heb je nog vragen rondom het verhuren bij Bookya.nl?\\\nNeem dan direct contact op met onze verhuur specialist:';

// Appends the 3 stat blocks to the "content shoot" (dark) section, found by
// its own body text rather than a heading (the section has no title field).
const addStatsRow = sections => {
  const idx = findSectionIndexByAnyText(sections, 'SietsOpStap');
  if (idx === -1) return sections;
  const existing = Array.isArray(sections[idx].blocks) ? sections[idx].blocks : [];
  const next = [...sections];
  next[idx] = { ...next[idx], blocks: [...existing, ...STATS_BLOCKS] };
  return next;
};

// bookya-verhuren.nl shows the dark "content shoot + stats" section BEFORE
// the pricing section; this page's Console content has them in the opposite
// order. Both sections already carry a real (pinned) sectionId by this
// point, so swapping their array position changes only what order they
// render in - their ids, and every id-based CSS rule and anchor link, are
// unaffected.
const reorderDarkBeforePricing = sections => {
  const darkIdx = findSectionIndexByAnyText(sections, 'SietsOpStap');
  // Note: by the time this runs, restructurePricingSection has already
  // promoted "Geen verrassingen..." from a block title to a real
  // section-level title (see restructurePricingSection above) - so this
  // looks it up with findSectionIndexByTitle now, not
  // findSectionIndexByBlockTitle.
  const pricingIdx = findSectionIndexByTitle(sections, PRICING_SECTION_TITLE_ANCHOR);
  if (darkIdx === -1 || pricingIdx === -1 || darkIdx < pricingIdx) return sections;
  const next = [...sections];
  const [dark] = next.splice(darkIdx, 1);
  next.splice(pricingIdx, 0, dark);
  return next;
};

// Replaces one block's callToAction text exactly (not a substring match,
// since short button labels like "Contact" are common words that could
// collide elsewhere) - used to fix the contact section's button, which
// Console has as "Contact" but bookya-verhuren.nl shows as "Neem contact
// op". Safe no-op if no block's callToAction matches exactly.
const replaceCallToActionText = (sections, oldText, newText) => {
  let replaced = false;
  const next = sections.map(s => {
    if (!Array.isArray(s?.blocks)) return s;
    const blocks = s.blocks.map(b => {
      if (!replaced && b?.callToAction?.content === oldText) {
        replaced = true;
        return { ...b, callToAction: { ...b.callToAction, content: newText } };
      }
      return b;
    });
    return { ...s, blocks };
  });
  return replaced ? next : sections;
};

// bookya-verhuren.nl's real hero button doesn't navigate to a separate
// signup page - it smoothly scrolls down the SAME page to the pricing
// section, where people can actually sign up from the pricing card's own
// button. This page's hero button was still using Console's own href
// ("/signup", a real page navigation) - changed here to "#section-3" (the
// pricing section's own pinned id, see pinSectionIds above) instead.
// Scoped specifically to the hero section (sectionId === 'section-1'), not
// matched by href/text value alone, because the pricing card's own
// "Vrijblijvend aanmelden" button also links to "/signup" and must be left
// completely untouched - people still need to be able to actually sign up
// from there. Safe no-op if the hero section or its callToAction is ever
// missing (e.g. Console content changes).
// No extra JS/CSS needed for the smooth-scroll part: this app's shared
// Link component (Primitives/Link/Link.js) already recognizes a "#..."
// href and routes it as a same-page hash link, and the app's own global
// hash-navigation handler (routing/Routes.js, setPageScrollPosition) always
// animates that with a real `scrollIntoView({ behavior: 'smooth' })` - both
// are existing, shared framework behavior, not something added here.
const HERO_SECTION_ID = 'section-1';
const PRICING_SECTION_ANCHOR = '#section-3';

const setHeroCtaToScrollToPricing = sections => {
  let replaced = false;
  const next = sections.map(s => {
    if (replaced || s?.sectionId !== HERO_SECTION_ID || !Array.isArray(s.blocks)) return s;
    const blocks = s.blocks.map(b => {
      if (!replaced && b?.callToAction?.href) {
        replaced = true;
        return { ...b, callToAction: { ...b.callToAction, href: PRICING_SECTION_ANCHOR } };
      }
      return b;
    });
    return { ...s, blocks };
  });
  return replaced ? next : sections;
};

// Ron asked for the "Neem contact op" button (the support section's CTA,
// previously navigating to Console's own "/p/contact" page) to open a
// mailto: link to contact@bookya.nl directly instead. Scoped to the
// support section (sectionId === 'section-5', pinned by pinSectionIds
// above) rather than matched by the button's text/href value alone, same
// pattern as setHeroCtaToScrollToPricing above - so this can't
// accidentally touch a different button if Console content changes. Safe
// no-op if that section or its callToAction is ever missing.
const CONTACT_SECTION_ID = 'section-5';
const CONTACT_MAILTO_HREF = 'mailto:contact@bookya.nl';

const setContactCtaToMailto = sections => {
  let replaced = false;
  const next = sections.map(s => {
    if (replaced || s?.sectionId !== CONTACT_SECTION_ID || !Array.isArray(s.blocks)) return s;
    const blocks = s.blocks.map(b => {
      if (!replaced && b?.callToAction?.href) {
        replaced = true;
        return { ...b, callToAction: { ...b.callToAction, href: CONTACT_MAILTO_HREF } };
      }
      return b;
    });
    return { ...s, blocks };
  });
  return replaced ? next : sections;
};

const buildVerhurenPageAssetsData = rawData => {
  if (!rawData || !Array.isArray(rawData.sections)) return rawData;
  const textFixed = applyTextFixes(rawData);
  const pinnedSections = pinSectionIds(textFixed.sections);
  const withCards = buildWaaromBookyaCards(pinnedSections);
  const withHeroParagraph = replaceBlockMarkdown(
    withCards,
    HERO_PARAGRAPH_ANCHOR,
    HERO_PARAGRAPH_MARKDOWN
  );
  const withHero = replaceBlockTitle(withHeroParagraph, HERO_TITLE_ANCHOR, HERO_TITLE_NEW);
  const withPricing = restructurePricingSection(withHero);
  const withDarkText = replaceBlockMarkdown(withPricing, 'SietsOpStap', DARK_SECTION_MARKDOWN);
  const withStats = addStatsRow(withDarkText);
  const reordered = reorderDarkBeforePricing(withStats);
  const withContactText = replaceCallToActionText(reordered, 'Contact', 'Neem contact op');
  const withContactCtaHref = setContactCtaToMailto(withContactText);
  const withHeroCta = setHeroCtaToScrollToPricing(withContactCtaHref);
  const sections = replaceBlockMarkdown(
    withHeroCta,
    SUPPORT_PARAGRAPH_ANCHOR,
    SUPPORT_PARAGRAPH_MARKDOWN
  );
  return { ...textFixed, sections };
};

/* --- Custom page-only footer (bookya-verhuren.nl's real footer: dark
   green, logo + tagline, 3 link columns and KVK/BTW details). This is very
   different from the app's site-wide footer - that one is a SHARED
   component (src/containers/FooterContainer, rendered by every page via
   PageBuilder.js) driven by one Console-managed "footer" asset, so
   restyling/recontenting it would change it on every page of the site
   (search, listings, account pages, etc.), not just this landing page.
   Per Ron's own choice, this new look is scoped to this one page only:
   the shared footer is hidden here (see ".verhurenPage :global(#footer)"
   in CMSPage.module.css) and this component is rendered in its place with
   this page's real business details (as supplied by Ron). The section/
   contact links reuse this page's own anchors, mirroring the "Voordelen /
   Kosten / Contact" links that used to live in the removed top sub-nav. */
const VERHUREN_FOOTER_NAV_LINKS = [
  { label: 'Voordelen', href: '#section-2' },
  { label: 'Kosten', href: '#section-3' },
  // Ron asked for this one to open a mailto: link too, same as the "Neem
  // contact op" CTA button above - reuses the same CONTACT_MAILTO_HREF
  // constant so both stay in sync if the address ever changes.
  { label: 'Contact', href: CONTACT_MAILTO_HREF },
];

const VERHUREN_FOOTER_CONTACT = {
  phone: '+31 (0)70 430 5895',
  phoneHref: 'tel:+31704305895',
  email: 'contact@bookya.nl',
  emailHref: 'mailto:contact@bookya.nl',
  addressLine1: 'Hoge Zand 39',
  addressLine2: '2512 EL Den Haag',
  kvk: '97342114',
  btw: 'NL0052.63.786.B.23',
};

const VERHUREN_FOOTER_INSTAGRAM_URL = 'https://www.instagram.com/bookya.nl/';

const VerhurenFooter = () => (
  <footer className={css.verhurenFooter}>
    <div className={css.verhurenFooterInner}>
      <div className={css.verhurenFooterBrand}>
        <div className={css.verhurenFooterLogo}>
          <Logo />
        </div>
        <p className={css.verhurenFooterTagline}>Bijzonder overnachten in het groen.</p>
      </div>

      <div className={css.verhurenFooterColumns}>
        <div className={css.verhurenFooterColumn}>
          <h3 className={css.verhurenFooterHeading}>Verhuren bij Bookya</h3>
          {VERHUREN_FOOTER_NAV_LINKS.map(link => (
            <a key={link.href} className={css.verhurenFooterLink} href={link.href}>
              {link.label}
            </a>
          ))}
        </div>

        <div className={css.verhurenFooterColumn}>
          <h3 className={css.verhurenFooterHeading}>Contact</h3>
          <a
            className={css.verhurenFooterContactRow}
            href={VERHUREN_FOOTER_CONTACT.phoneHref}
          >
            <span
              className={`${css.verhurenFooterContactIcon} ${css.verhurenFooterIconPhone}`}
            />
            {VERHUREN_FOOTER_CONTACT.phone}
          </a>
          <a
            className={css.verhurenFooterContactRow}
            href={VERHUREN_FOOTER_CONTACT.emailHref}
          >
            <span
              className={`${css.verhurenFooterContactIcon} ${css.verhurenFooterIconMail}`}
            />
            {VERHUREN_FOOTER_CONTACT.email}
          </a>
          <span className={css.verhurenFooterContactRow}>
            <span
              className={`${css.verhurenFooterContactIcon} ${css.verhurenFooterIconPin}`}
            />
            <span>
              {VERHUREN_FOOTER_CONTACT.addressLine1}
              <br />
              {VERHUREN_FOOTER_CONTACT.addressLine2}
            </span>
          </span>
          <p className={css.verhurenFooterLegal}>
            KVK-nummer: {VERHUREN_FOOTER_CONTACT.kvk}
            <br />
            BTW-nummer: {VERHUREN_FOOTER_CONTACT.btw}
          </p>
        </div>

        <div className={css.verhurenFooterColumn}>
          <h3 className={css.verhurenFooterHeading}>Volg ons</h3>
          <a
            className={css.verhurenFooterContactRow}
            href={VERHUREN_FOOTER_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span
              className={`${css.verhurenFooterContactIcon} ${css.verhurenFooterIconInstagram}`}
            />
            Instagram
          </a>
        </div>
      </div>
    </div>

    <div className={css.verhurenFooterBottom}>
      <span>&copy; {new Date().getFullYear()} Bookya. Alle rechten voorbehouden.</span>
      <div className={css.verhurenFooterBottomLinks}>
        <a href="/p/privacy">Privacybeleid</a>
        <a href="/p/voorwaarden">Voorwaarden</a>
      </div>
    </div>
  </footer>
);

export const CMSPageComponent = props => {
  const { params, pageAssetsData, inProgress, error } = props;
  const pageId = params.pageId || props.pageId;
  const pageSpecificClass = PAGE_SPECIFIC_CLASSES[pageId];

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const rawPageData = pageAssetsData?.[pageId]?.data;
  const pageData =
    pageId === 'vakantiewoning-verhuren' ? buildVerhurenPageAssetsData(rawPageData) : rawPageData;

  const pageBuilder = (
    <PageBuilder
      pageAssetsData={pageData}
      inProgress={inProgress}
      schemaType="Article"
      featuredListings={getFeaturedListingsProps(pageId, props)}
    />
  );

  if (!pageSpecificClass) {
    return pageBuilder;
  }

  return (
    <div className={pageSpecificClass}>
      {pageBuilder}
      {pageId === 'vakantiewoning-verhuren' ? <VerhurenFooter /> : null}
    </div>
  );
};

CMSPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const CMSPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(CMSPageComponent);

export default CMSPage;
